// Stage registry — single source of truth.
//
// Adding a new stage = create a *Stage.jsx file in this dir, import it here,
// and add one entry to STAGES. Order matters: it drives the carousel order.
//
// ─── Stage Strategy contract ─────────────────────────────────────────────────
//   Each stage component receives { state, journey, update } and renders
//   only its body content (the title, blurb, status pill, and back/next nav
//   are owned by <StageShell/>).
//
//   props.state    — { status: 'pending'|'in_progress'|'done', data: any }
//   props.journey  — full journey state (read-only). Read prior stages' data
//                    here to pre-fill — never re-ask the seller for input
//                    already captured upstream.
//   props.update   — (patch) => void  | merges patch into this stage's state.
//                    The orchestrator persists to localStorage automatically.
//
// ─── Auto-run on entry (seamless) ────────────────────────────────────────────
//   Per the "seamless" north star, AI-backed stages must auto-run their work
//   on first mount via useEffect — the seller should arrive to results, not
//   to a "Run analysis" button. Reserve explicit triggers for regenerate.

import ValuationStage from "./ValuationStage";
import PrepStage from "./PrepStage";
import DisclosuresStage from "./DisclosuresStage";
import StagingStage from "./StagingStage";
import PhotographyStage from "./PhotographyStage";
import ListingStage from "./ListingStage";
import MlsStage from "./MlsStage";
import ShowingsStage from "./ShowingsStage";
import OpenHouseStage from "./OpenHouseStage";
import OffersStage from "./OffersStage";
import EscrowStage from "./EscrowStage";
import ClosingStage from "./ClosingStage";

export const STAGES = [
  {
    id: "valuation",
    short: "Price",
    title: "Fair-price valuation",
    blurb: "Live comps pulled and an AI fair-price range produced — so you list with confidence, not guesswork.",
    Component: ValuationStage,
  },
  {
    id: "prep",
    short: "Prep",
    title: "Pre-listing prep",
    blurb: "AI ranks the small fixes that materially move sale price. Vendor matches for the work, billed at close.",
    Component: PrepStage,
  },
  {
    id: "disclosures",
    short: "Disclose",
    title: "Disclosures",
    blurb: "AI walks you through plain-English questions; we fill TDS, SPQ, NHD, lead-paint, and HOA paperwork in the background.",
    Component: DisclosuresStage,
    // Only synthesize() sets status:"done" (after all questions are answered) —
    // don't let the shared advance button force-complete this stage early.
    requiresManualDone: true,
  },
  {
    id: "staging",
    short: "Stage",
    title: "Staging",
    blurb: "Pick virtual, light, or full staging — our vendor network handles scheduling, payment, and warranty.",
    Component: StagingStage,
  },
  {
    id: "photography",
    short: "Photo",
    title: "Photography",
    blurb: "Pro photo shoot + floor plan + walkthrough video. We pick the hero shot and the order.",
    Component: PhotographyStage,
  },
  {
    id: "listing",
    short: "Listing",
    title: "Listing creation",
    blurb: "AI drafts your listing copy from your property data and valuation. Review, edit, approve.",
    Component: ListingStage,
  },
  {
    id: "mls",
    short: "MLS",
    title: "MLS & syndication",
    blurb: "We file with the regional MLS through our broker-of-record. Your listing syndicates to all major portals.",
    Component: MlsStage,
    // Only submit() sets status:"done" (after ownership verification) — don't
    // let the shared advance button force-complete this stage early.
    requiresManualDone: true,
  },
  {
    id: "showings",
    short: "Tours",
    title: "Showings",
    blurb: "Private tour scheduling, lockbox + sign-in handled. AI summarizes feedback per visit.",
    Component: ShowingsStage,
  },
  {
    id: "openhouse",
    short: "Open",
    title: "Open house",
    blurb: "First weekend event. AI generates social, email, and door-hanger marketing copy tied to your listing.",
    Component: OpenHouseStage,
  },
  {
    id: "offers",
    short: "Offers",
    title: "Offers & negotiation",
    blurb: "AI scores incoming offers on net proceeds + close probability, drafts counter strategy.",
    Component: OffersStage,
  },
  {
    id: "escrow",
    short: "Escrow",
    title: "Escrow & contingencies",
    blurb: "Open escrow with our title partner. AI parses the buyer's inspection PDF and translates repair requests into seller-friendly counters.",
    Component: EscrowStage,
  },
  {
    id: "closing",
    short: "Close",
    title: "Closing & signing",
    blurb: "Final docs DocuSign-ready. AI line-checks your settlement statement. Keys handed off, equity in hand.",
    Component: ClosingStage,
  },
];

export const STAGE_COUNT = STAGES.length;
export const getStageMeta = (id) => STAGES.find((s) => s.id === id);
